import type {
  ComponentInteraction,
  InteractionTriggerType,
  InteractionCondition,
  InteractionAction,
} from '@/types/interactions';
import { logger } from '@/utils/logger';

/**
 * Declarative Component Interaction Runtime.
 * Evaluates conditions and executes actions when triggers fire.
 * Active only in preview/published mode.
 */
class InteractionRuntimeImpl {
  private static instance: InteractionRuntimeImpl;

  static getInstance(): InteractionRuntimeImpl {
    if (!InteractionRuntimeImpl.instance) {
      InteractionRuntimeImpl.instance = new InteractionRuntimeImpl();
    }
    return InteractionRuntimeImpl.instance;
  }

  /**
   * Handle a trigger event on a component.
   * Evaluates conditions and executes matching actions.
   */
  async handleTrigger(
    componentId: string,
    triggerType: InteractionTriggerType,
    eventData: Record<string, unknown>,
    interactions: ComponentInteraction[],
    context: InteractionContext,
  ): Promise<void> {
    const matching = interactions.filter(
      i => i.enabled && i.trigger.type === triggerType,
    );

    if (matching.length > 0) {
      logger.info(`[Phase6:Interaction] Trigger fired`, { componentId, triggerType, matchCount: matching.length });
    }

    for (const interaction of matching) {
      if (this.evaluateConditions(interaction.conditions, componentId, context)) {
        for (const action of interaction.actions) {
          await this.executeAction(action, componentId, context);
        }
      }
    }
  }

  private evaluateConditions(
    conditions: InteractionCondition[],
    sourceComponentId: string,
    context: InteractionContext,
  ): boolean {
    if (conditions.length === 0) return true;

    const result = conditions.every(condition => {
      switch (condition.type) {
        case 'always':
          return true;

        case 'viewport':
          return condition.viewport === context.currentBreakpoint;

        case 'component-value': {
          const targetId = condition.sourceComponentId || sourceComponentId;
          const component = context.getComponent(targetId);
          if (!component) return false;
          const prop = condition.sourceProperty || 'value';
          const value = (component.properties as Record<string, unknown>)?.[prop];
          return this.compare(value, condition.operator, condition.value);
        }

        case 'data-field': {
          const fieldValue = context.getDataFieldValue?.(condition.dataField || '');
          return this.compare(fieldValue, condition.operator, condition.value);
        }

        default:
          return false;
      }
    });
    if (!result) {
      logger.debug(`[Phase6:Interaction] Conditions not met`, { sourceComponentId, conditionCount: conditions.length });
    }
    return result;
  }

  private compare(actual: unknown, operator: string, expected: unknown): boolean {
    switch (operator) {
      case 'equals':
        return actual === expected;
      case 'not-equals':
        return actual !== expected;
      case 'greater-than':
        return Number(actual) > Number(expected);
      case 'less-than':
        return Number(actual) < Number(expected);
      case 'contains':
        return String(actual).includes(String(expected));
      case 'is-empty':
        return actual === null || actual === undefined || actual === '';
      case 'is-not-empty':
        return actual !== null && actual !== undefined && actual !== '';
      default:
        return false;
    }
  }

  private async executeAction(
    action: InteractionAction,
    _sourceComponentId: string,
    context: InteractionContext,
  ): Promise<void> {
    logger.info(`[Phase6:Interaction] Executing action`, { actionType: action.type, target: action.targetComponentId || action.targetPageId || action.url });
    switch (action.type) {
      case 'navigate-page':
        if (action.targetPageId) {
          context.navigateToPage(action.targetPageId);
        }
        break;

      case 'show-component':
        if (action.targetComponentId) {
          context.setComponentVisibility(action.targetComponentId, true);
        }
        break;

      case 'hide-component':
        if (action.targetComponentId) {
          context.setComponentVisibility(action.targetComponentId, false);
        }
        break;

      case 'toggle-component':
        if (action.targetComponentId) {
          const comp = context.getComponent(action.targetComponentId);
          if (comp) {
            context.setComponentVisibility(action.targetComponentId, !!comp.hidden);
          }
        }
        break;

      case 'update-property':
        if (action.targetComponentId && action.property !== undefined) {
          context.updateComponentProperty(
            action.targetComponentId,
            action.property,
            action.value,
          );
        }
        break;

      case 'refresh-data':
        if (action.targetComponentId) {
          context.refreshComponentData?.(action.targetComponentId);
        }
        break;

      case 'open-url':
        if (action.url) {
          if (action.openInNewTab) {
            window.open(action.url, '_blank', 'noopener,noreferrer');
          } else {
            window.location.href = action.url;
          }
        }
        break;

      case 'scroll-to':
        if (action.targetComponentId) {
          const el = document.querySelector(`[data-component-id="${action.targetComponentId}"]`);
          el?.scrollIntoView({ behavior: action.scrollBehavior || 'smooth' });
        }
        break;

      case 'fetch-schema-data': {
        if (!action.schemaId || !action.groupId) {
          logger.warn('[Interaction] fetch-schema-data missing required fields', { action });
          break;
        }

        if (action.targetDataComponentIds) {
          const { useComponentDataStore } = await import('@/stores/editor/component-data-store');
          for (const targetId of action.targetDataComponentIds) {
            useComponentDataStore.getState().setLoading(targetId, true);
          }
        }

        try {
          const { default: dataApi } = await import('@/services/api/dataApi');
          const { records } = await dataApi.queryRecords(action.groupId, action.schemaId, {
            limit: action.fetchLimit ?? 100,
          });

          // Flatten IDataRecord envelopes: promote record.data.* fields to top level
          // so table columns with accessorKey "weight" can find the value directly
          const flatRecords = records.map((record: Record<string, unknown>) => {
            if (
              record.data && typeof record.data === 'object' && !Array.isArray(record.data) &&
              ('schemaId' in record || 'schemaVersion' in record)
            ) {
              const { data, schemaId, schemaVersion, groupId, organizationId, metadata, __v, ...rest } = record;
              return { ...rest, ...(data as Record<string, unknown>) };
            }
            return record;
          });

          if (action.targetDataComponentIds) {
            for (const targetId of action.targetDataComponentIds) {
              context.setComponentData?.(targetId, flatRecords);
            }
          }
        } catch (error) {
          logger.error('[Interaction] fetch-schema-data failed', { error });
          if (action.targetDataComponentIds) {
            const { useComponentDataStore } = await import('@/stores/editor/component-data-store');
            for (const targetId of action.targetDataComponentIds) {
              useComponentDataStore.getState().setError(
                targetId,
                (error as Error).message || 'Failed to fetch data'
              );
            }
          }
        }
        break;
      }

      case 'submit-form-data':
        // Handled by SharedButtonComponent's handleClick switch-dispatch, not InteractionRuntime.
        // This case exists to prevent the "unknown action type" warning.
        logger.debug('[Interaction] submit-form-data is handled by Button component directly');
        break;
    }
  }
}

export interface InteractionContext {
  currentBreakpoint: 'mobile' | 'tablet' | 'desktop';
  getComponent: (id: string) => { properties: Record<string, unknown>; hidden: boolean } | null;
  navigateToPage: (pageId: string) => void;
  setComponentVisibility: (componentId: string, visible: boolean) => void;
  updateComponentProperty: (componentId: string, property: string, value: unknown) => void;
  getDataFieldValue?: (field: string) => unknown;
  refreshComponentData?: (componentId: string) => void;
  getInputValue?: (componentId: string) => unknown;
  setInputValue?: (componentId: string, value: unknown) => void;
  showNotification?: (message: string, type: 'success' | 'error' | 'info') => void;
  setComponentData?: (componentId: string, data: Record<string, unknown>[]) => void;
}

export const interactionRuntime = InteractionRuntimeImpl.getInstance();
